import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLenis } from "lenis/react";
import { PRICE_LIST } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

export default function Services() {
  const [active, setActive] = useState(0);
  const lenis = useLenis();

  const goBooking = (e) => {
    e.preventDefault();
    lenis?.scrollTo("#prenota", { duration: 1.8 });
  };

  return (
    <section id="servizi" data-testid="services-section" className="bg-blush-50/60 py-28 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-blush-500"
        >
          <span className="h-px w-10 bg-blush-500" />
          Il listino
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="max-w-2xl font-serif text-4xl leading-tight text-ink sm:text-5xl lg:text-6xl"
        >
          Trattamenti e <span className="italic text-blush-500">prezzi</span>, in chiaro.
        </motion.h2>

        <div className="mt-20 grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="space-y-20 lg:col-span-7">
            {PRICE_LIST.map((cat, ci) => (
              <motion.div
                key={cat.id}
                data-testid={`listino-category-${cat.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: EASE }}
                onMouseEnter={() => setActive(ci)}
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-sm italic text-blush-500">{cat.num}</span>
                  <h3 className="font-serif text-2xl text-ink lg:text-3xl">{cat.category}</h3>
                </div>
                <p className="mb-4 mt-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-blush-500">
                  {cat.note}
                </p>
                <ul className="border-t border-blush-100">
                  {cat.items.map((item, ii) => (
                    <li
                      key={ii}
                      data-testid={`listino-item-${cat.id}-${ii}`}
                      className="group flex items-baseline gap-3 border-b border-blush-100 py-5 transition-colors duration-300"
                    >
                      <div className="min-w-0">
                        <p className="text-base font-medium text-ink transition-colors duration-300 group-hover:text-blush-600">
                          {item.name}
                        </p>
                        {item.note && <p className="mt-1 text-xs text-ink-soft">{item.note}</p>}
                      </div>
                      <span aria-hidden className="mx-2 flex-1 -translate-y-1 border-b border-dotted border-blush-500/40" />
                      <span
                        data-testid={`listino-price-${cat.id}-${ii}`}
                        className="shrink-0 font-serif text-xl italic text-ink lg:text-2xl"
                      >
                        {item.price}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: EASE }}
            >
              <a
                href="#prenota"
                onClick={goBooking}
                data-testid="listino-cta-prenota"
                className="group inline-flex items-center gap-3 rounded-full bg-ink px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-blush-50 transition-colors duration-500 hover:bg-blush-600"
              >
                Prenota il tuo trattamento
                <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
              </a>
            </motion.div>
          </div>

          <div className="hidden lg:col-span-5 lg:block">
            <div className="sticky top-32">
              <div className="relative overflow-hidden rounded-t-full" style={{ aspectRatio: "3/4" }} data-testid="services-image-frame">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={active}
                    src={PRICE_LIST[active].img}
                    alt={PRICE_LIST[active].category}
                    initial={{ opacity: 0, scale: 1.08 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 rounded-t-full ring-1 ring-inset ring-blush-100" />
              </div>
              <p className="mt-6 text-center text-[10px] font-semibold uppercase tracking-[0.35em] text-ink-soft">
                {PRICE_LIST[active].num} — {PRICE_LIST[active].category}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
